import { endOfDay, format, startOfDay, subDays } from "date-fns";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { Download } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useReactTable, getCoreRowModel, getPaginationRowModel, type ColumnDef, flexRender } from "@tanstack/react-table";

import { Credits } from "@/components/card/credits";
import { PageHeader } from "@/components/layout/heading";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { SubscriptionApiFactory } from "@/apis/subscription";
import { useSessionUser } from "@/hooks/use-session";
import { createOrCurrentDate, downloadFile } from "@/lib/utils";
import { CreditLog } from "@/schema/subscription";

export const Route = createFileRoute("/dashboard/settings/_layout/usage")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="min-h-full w-full @container">
      <PageHeader title="Usage" description="View your usage and purchase additional credits" className="mb-7 hidden lg:block" />
      <UsageSettings />
    </div>
  );
}

type DateRangeFilter = "today" | "yesterday" | "last-7d" | "last-30d" | "last-90d";

interface UsageLogRow extends CreditLog {
  user_name: string;
}

function UsageSettings() {
  const user = useSessionUser();
  const [range, setRange] = useState<DateRangeFilter>("last-30d");

  const fetchDateRange = useCallback((filter: DateRangeFilter): { start_date: string; end_date: string } => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = endOfDay(now);

    switch (filter) {
      case "today":
        startDate = startOfDay(now);
        break;
      case "yesterday":
        startDate = startOfDay(subDays(now, 1));
        endDate = endOfDay(subDays(now, 1));
        break;
      case "last-7d":
        startDate = startOfDay(subDays(now, 7));
        break;
      case "last-30d":
        startDate = startOfDay(subDays(now, 30));
        break;
      case "last-90d":
        startDate = startOfDay(subDays(now, 90));
        break;
      default:
        startDate = startOfDay(subDays(now, 30));
    }

    return { start_date: startDate.toISOString(), end_date: endDate.toISOString() };
  }, []);

  const { data: logs = [], isLoading } = useQuery(
    SubscriptionApiFactory.Queries.FetchCreditLogs(fetchDateRange(range), {
      queryKey: [...SubscriptionApiFactory.Keys.FetchCreditLogs(), range],
    })
  );

  const filteredLogs = useMemo(() => {
    return logs.map((log) => ({ ...log, user_name: user.name }));
  }, [logs, user.name]);

  const columns = useMemo<ColumnDef<UsageLogRow>[]>(
    () => [
      {
        accessorKey: "created_at",
        header: "Date",
        cell: ({ row }) => {
          return <span className="font-medium">{format(createOrCurrentDate(row.original.created_at), "MMM d, yyyy")}</span>;
        },
      },
      {
        accessorKey: "user_name",
        header: "User",
        cell: ({ row }) => <span className="capitalize">{row.original.user_name}</span>,
      },
      {
        accessorKey: "tool_name",
        header: "Tool",
        cell: ({ row }) => <span className="capitalize">{row.original.tool_name?.split("-").join(" ") || "N/A"}</span>,
      },
      {
        accessorKey: "operation",
        header: "Operation",
        cell: ({ row }) => <span className="font-medium">{row.original.operation}</span>,
      },
      {
        accessorKey: "credit",
        header: "Credits",
        cell: ({ row }) => {
          return (
            <div className="text-right text-base font-medium">
              <CreditCell credit={row.original.credit} operation={row.original.operation} />
            </div>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredLogs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  // Reset pagination to first page when date range filter changes
  useEffect(() => {
    table.setPageIndex(0);
  }, [range, table]);

  const handleExport = () => {
    const headers = ["Date", "User", "Tool", "Credits", "Status"];
    const content = [headers.join(","), ...filteredLogs.map((log) => [log.created_at, log.user_name, log.tool_name || "", log.credit, log.operation].join(","))].join("\n");

    const blob = new Blob([content], { type: "text/csv" });
    downloadFile(blob, "usage-events-" + Date.now() + ".csv");
  };

  return (
    <Fragment>
      <Credits />
      <section className="flex flex-col gap-y-6 mt-8 md:mt-12">
        <PageHeader title="All Events" description="View all usage events and purchases." className="flex-col sm:flex-row items-start sm:items-end gap-y-3.5">
          <div className="flex items-center justify-end gap-2.5 overflow-x-auto scrollbar-hidden">
            <Select value={range} onValueChange={(value) => setRange(value as DateRangeFilter)}>
              <SelectTrigger className="outline-none focus-visible:ring-0 w-full sm:w-fit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="last-7d">Last 7 days</SelectItem>
                <SelectItem value="last-30d">Last 30 days</SelectItem>
                <SelectItem value="last-90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleExport}>
              <Download className="size-4 mr-2" weight="regular" />
              Export CSV
            </Button>
          </div>
        </PageHeader>
        <div className="bg-background border border-border rounded-lg overflow-x-auto scrollbar-hidden">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent border-border">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className={header.column.id === "credit" ? "text-muted-foreground font-medium text-right" : "text-muted-foreground font-medium"}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index} className="border-border">
                    {columns.map((_, colIndex) => (
                      <TableCell key={colIndex}>
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && "selected"} className="border-border hover:bg-muted/50">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {!isLoading && filteredLogs.length > 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-center sm:justify-between gap-3.5">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to&nbsp;
              {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, filteredLogs.length)} of {filteredLogs.length} results
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} className="flex-1 min-w-24">
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} className="flex-1 min-w-24">
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </section>
    </Fragment>
  );
}

interface CreditCellProps {
  credit: number;
  operation: CreditLog["operation"];
}

function CreditCell({ credit, operation }: CreditCellProps) {
  switch (operation) {
    case "CONSUME":
      return <span className="text-red-500">- {credit}</span>;
    case "REFUND":
      return <span className="text-green-500">+ {credit}</span>;
    case "TOPUP":
      return <span className="text-blue-500">+ {credit}</span>;
    case "EXPIRED":
      return <span className="text-gray-500">- {credit}</span>;
  }
}
